import { groupBy } from 'lodash'
import moment from 'moment'
import type { CSSProperties, ReactElement } from 'react'
import { Button } from 'app/components/ui/button'
import { cn } from 'app/lib/utils'
import { VisitRow } from './CalendarVisit'
import type { AppointmentsCalendarState } from './useAppointmentsCalendar'

const DAY_MIN_PX = 200

/** The week as one list per day. A week of a busy practice is too many visits to draw to scale side by side. */
export function CalendarWeek({ calendar }: { calendar: AppointmentsCalendarState }): ReactElement {
  const byDay = groupBy(calendar.visits, (visit) => visit.day)
  // A weekend day earns a column only when something is booked on it.
  const days = calendar.range.days.filter((day) => moment(day).isoWeekday() <= 5 || calendar.counts.has(day))

  return (
    <div className="min-h-0 flex-1 overflow-x-auto rounded-lg border border-default bg-surface">
      <div
        className="grid h-full min-w-(--week-min-width) grid-cols-(--week-columns)"
        style={
          {
            '--week-columns': `repeat(${days.length}, minmax(${DAY_MIN_PX}px, 1fr))`,
            '--week-min-width': `${days.length * DAY_MIN_PX}px`,
          } as CSSProperties
        }
      >
        {days.map((day) => {
          const visits = byDay[day] ?? []
          const isToday = day === calendar.today
          return (
            <section key={day} className="flex min-h-0 min-w-0 flex-col border-l border-default first:border-l-0">
              <div className="shrink-0 border-b border-default p-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="row"
                  title="Open this day"
                  onClick={() => calendar.goTo(day, `day`)}
                  className="justify-between"
                >
                  <span className="flex items-center gap-1.5 text-body-sm font-semibold text-strong">
                    {moment(day).format(`ddd`)}
                    <span className={cn(`rounded px-1 tabular-nums`, isToday && `bg-inverse text-inverse`)}>
                      {moment(day).format(`D`)}
                    </span>
                  </span>
                  <span className="text-body-xs tabular-nums text-subtle">
                    {visits.length} {visits.length === 1 ? `visit` : `visits`}
                  </span>
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-1">
                {visits.length ? (
                  visits.map((visit) => (
                    <VisitRow key={visit.appointment.id} visit={visit} calendar={calendar} withProvider />
                  ))
                ) : (
                  <p className="px-2 py-3 text-body-xs text-minimal">Nothing booked</p>
                )}
              </div>
            </section>
          )
        })}
      </div>
    </div>
  )
}
